/* eslint-disable camelcase */
import { invisiblyApi } from '../../../redux/api/invisibly-api';
import { InvisiblyApiResponse } from '../../../services/api/api-response.types';
import { PacingViewApiResponse, PacingViewsListResponse } from './pacing.types';
import { APITagType } from '../../../redux/api/invisibly-api.types';
import { AdvertiserNode, LineItem } from '../types/pacing.types';
import { patchLineAndRecompute, setLineItemUpdateData } from '../utils/pacing-utils';

const pacingEndpoints = invisiblyApi.injectEndpoints({
  endpoints: (builder) => ({
    createPacingView: builder.mutation<any, { name: string }>({
      query: ({ name }) => ({
        method: 'POST',
        url: 'b2b/pacing/views',
        data: {
          name,
        },
      }),
      transformResponse(response: InvisiblyApiResponse<PacingViewApiResponse>) {
        return response.item;
      },
      invalidatesTags: [APITagType.PacingView],
    }),
    getPacingViews: builder.query<PacingViewsListResponse, void>({
      query: () => ({
        method: 'get',
        url: 'b2b/pacing/views',
      }),
      transformResponse(response: InvisiblyApiResponse<PacingViewsListResponse>) {
        return response.item;
      },
      providesTags: [APITagType.PacingView],
    }),
    getPacingPlatforms: builder.query<string[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/reporter/filter/sources',
      }),
      transformResponse(response: InvisiblyApiResponse<{ sources: string[] }>) {
        return response.item.sources;
      },
    }),
    getPacingData: builder.query<AdvertiserNode[], { viewId: string, refresh?: boolean }>({
      query: ({ viewId, refresh }) => ({
        timeout: 60000,
        method: 'get',
        url: `b2b/pacing/views/${viewId}?refresh=${refresh ?? false}`,
      }),
      transformResponse(response: InvisiblyApiResponse<AdvertiserNode[]>) {
        return response.item;
      },
      providesTags: [APITagType.PacingView],
      // Ensure different cache keys for different platform/view combinations
      serializeQueryArgs: ({ queryArgs }) => {
        return `${queryArgs.viewId}`;
      },
    }),
    updateLineItem: builder.mutation<void, { lineItem: LineItem; field: string; value: any; viewId: string }>({
      query: ({ lineItem, field, value, viewId }) => ({
        method: 'PATCH',
        url: `b2b/pacing/items/${lineItem.id}`,
        data: setLineItemUpdateData(lineItem, field, value),
      }),
      async onQueryStarted({ lineItem, field, value, viewId }, { queryFulfilled, dispatch }) {
        const patchResult = dispatch(
          pacingEndpoints.util.updateQueryData('getPacingData', { viewId }, (draft) => {
            patchLineAndRecompute(draft, lineItem.id, field as keyof LineItem, value);
          })
        );

        try {
          await queryFulfilled;
          // Optional: also invalidate to re-validate from server
          // dispatch(pacingApi.util.invalidateTags([{ type: 'PacingView', id: viewId }]));
        } catch {
          patchResult.undo();
        }

      },
    }),
    updatePacingView: builder.mutation<any, { viewId: string }>({
      query: ({ viewId }) => ({
        method: 'POST',
        url: `b2b/pacing/views/${viewId}/update`,
      }),
      invalidatesTags: [APITagType.PacingView],
    }),
    updatePacingViewDates: builder.mutation<any, { viewId: string, startDate: string, endDate: string }>({
      query: ({ viewId, startDate, endDate }) => ({
        method: 'POST',
        url: `b2b/pacing/views/${viewId}/dates`,
        data: {
          start_date: startDate,
          end_date: endDate,
        },
      }),
      invalidatesTags: [APITagType.PacingView],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetPacingViewsQuery,
  useGetPacingPlatformsQuery,
  useGetPacingDataQuery,
  useUpdateLineItemMutation,
  useUpdatePacingViewMutation,
  useUpdatePacingViewDatesMutation,
  useCreatePacingViewMutation,
} = pacingEndpoints;